import { useEffect, useRef, useState } from "react";
import { Play, Loader2, Volume2 } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

export const VOICES = [
  { id: "onyx", label: "Deep", sub: "Onyx", sample: "Hey, it's your coach. Deep and steady. Let's get you out of that job, one move at a time." },
  { id: "coral", label: "Warm", sub: "Coral", sample: "Hi, it's your coach. Warm and in your corner. We build your way out together, starting today." },
];

export const SENSITIVITY = [
  { id: "sensitive", label: "Sensitive", hint: "Cuts in fast" },
  { id: "normal", label: "Normal", hint: "Balanced" },
  { id: "quiet", label: "Quiet Room Off", hint: "Best for noisy places" },
];

const token = () => localStorage.getItem("ihmj_token");

export default function VoicePicker() {
  const [voice, setVoice] = useState(() => localStorage.getItem("ihmj_coach_voice") || "onyx");
  const [sens, setSens] = useState(() => localStorage.getItem("ihmj_barge_sensitivity") || "normal");
  const [busy, setBusy] = useState(null);
  const [playing, setPlaying] = useState(null);
  const playerRef = useRef(null);

  useEffect(() => { localStorage.setItem("ihmj_coach_voice", voice); }, [voice]);
  useEffect(() => { localStorage.setItem("ihmj_barge_sensitivity", sens); }, [sens]);
  useEffect(() => () => { try { playerRef.current?.pause(); } catch {} }, []);

  const preview = async (v) => {
    try { playerRef.current?.pause(); } catch {}
    setPlaying(null);
    setBusy(v.id);
    try {
      const res = await fetch(`${API}/coach/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ text: v.sample, voice: v.id }),
      });
      if (!res.ok) throw new Error("sample failed");
      const buf = await res.arrayBuffer();
      const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
      const audio = new Audio(url);
      playerRef.current = audio;
      const done = () => { URL.revokeObjectURL(url); setPlaying(null); };
      audio.onended = done;
      audio.onerror = done;
      setBusy(null);
      setPlaying(v.id);
      await audio.play().catch(() => done());
    } catch {
      setBusy(null);
      toast.error("Couldn't play a sample. Try again.");
    }
  };

  return (
    <div className="mt-4 card-panel p-4" data-testid="voice-picker-panel">
      <div className="eyebrow text-[#FD0F10]">COACH VOICE</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {VOICES.map((v) => (
          <div
            key={v.id}
            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${voice === v.id ? "border-[#FD0F10] bg-[#FD0F10]/10" : "border-[#2A2E39]"}`}
          >
            <button onClick={() => setVoice(v.id)} className="flex items-center gap-2 text-left flex-1" data-testid={`voice-select-${v.id}`}>
              <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${voice === v.id ? "border-[#FD0F10] bg-[#FD0F10]" : "border-gray-500"}`} />
              <span className="text-sm text-gray-100 font-semibold">{v.label}</span>
              <span className="text-xs text-gray-500 font-mono-ui uppercase tracking-wider">{v.sub}</span>
            </button>
            <button
              onClick={() => preview(v)}
              disabled={busy === v.id}
              className="shrink-0 w-9 h-9 rounded-full border border-[#2A2E39] flex items-center justify-center text-gray-300 hover:border-[#FD0F10] hover:text-white disabled:opacity-50"
              aria-label={`Hear the ${v.label} voice`}
              title="Hear a sample"
              data-testid={`voice-preview-${v.id}`}
            >
              {busy === v.id ? <Loader2 size={16} className="animate-spin" /> : playing === v.id ? <Volume2 size={16} className="text-[#FD0F10]" /> : <Play size={16} />}
            </button>
          </div>
        ))}
      </div>

      <div className="eyebrow text-[#FD0F10] mt-4">INTERRUPT SENSITIVITY</div>
      <p className="text-xs text-gray-500 mt-1">How easily talking over the coach cuts it off. Turn it down for a noisy commute.</p>
      <div className="mt-2 flex gap-2 flex-wrap" data-testid="barge-sensitivity">
        {SENSITIVITY.map((s) => (
          <button
            key={s.id}
            onClick={() => setSens(s.id)}
            className={`px-3 py-2 rounded-lg border text-xs font-mono-ui uppercase tracking-wider ${sens === s.id ? "border-[#FD0F10] bg-[#FD0F10]/10 text-white" : "border-[#2A2E39] text-gray-400 hover:text-white"}`}
            data-testid={`sens-${s.id}`}
          >
            {s.label}
            <span className="block text-[10px] normal-case tracking-normal text-gray-500 mt-0.5">{s.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
